import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useCompany } from '../contexts/CompanyContext';
import { useAuth } from '../contexts/AuthContext';
import { BuildingOfficeIcon, MagnifyingGlassIcon, CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

interface CompanySelectorProps {
  /** Full-width labeled trigger (sidebar expanded, MobileNav) — default.
   * Set false for the collapsed rail's icon-only popover trigger. */
  expanded?: boolean;
}

const CompanySelector: React.FC<CompanySelectorProps> = ({ expanded = true }) => {
  const { user } = useAuth();
  const { selectedCompanyId, companies, isLoading, setSelectedCompanyId } = useCompany();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [coords, setCoords] = useState<{ top: number; left: number; width: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current && !dropdownRef.current.contains(target) &&
        triggerRef.current && !triggerRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Only show for eskimi_admin and adops users (including when impersonating)
  // When impersonating, the 'user' object is the impersonated user, so we just check their role
  if (!user || (user.role !== 'eskimi_admin' && user.role !== 'adops')) {
    return null;
  }

  // Filter companies based on search query
  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedCompany = companies.find(c => c.id === selectedCompanyId);

  const handleToggleOpen = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 8, left: rect.left, width: rect.width });
    }
    setIsOpen((prev) => !prev);
  };

  const handleCompanySelect = (companyId: string) => {
    setSelectedCompanyId(companyId);
    setIsOpen(false);
    setSearchQuery('');
  };

  const dropdown = isOpen && coords && createPortal(
    <div
      ref={dropdownRef}
      style={{ position: 'fixed', top: coords.top, left: coords.left, width: Math.max(coords.width, 288) }}
      className="z-50 flex max-h-96 flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-lg"
    >
      {/* Search input */}
      <div className="p-3 border-b border-line">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search companies..."
            className="w-full rounded-md border border-line bg-canvas pl-9 pr-3 py-2 text-sm text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
      </div>

      {/* Company list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 text-center text-sm text-ink-muted">Loading companies...</div>
        ) : filteredCompanies.length === 0 ? (
          <div className="p-4 text-center text-sm text-ink-muted">No companies found</div>
        ) : (
          <div className="py-1">
            {filteredCompanies.map((company) => (
              <button
                key={company.id}
                onClick={() => handleCompanySelect(company.id)}
                className={`flex w-full items-center justify-between px-4 py-2 text-left text-sm transition-colors hover:bg-ghost-bg ${
                  company.id === selectedCompanyId ? 'bg-nav-active' : ''
                }`}
              >
                <div className="flex-1">
                  <div className={`font-medium ${company.id === selectedCompanyId ? 'text-accent' : 'text-ink'}`}>
                    {company.name}
                  </div>
                  {company.advertiserId && (
                    <div className="mt-0.5 text-xs text-ink-muted">Advertiser ID: {company.advertiserId}</div>
                  )}
                </div>
                {company.id === selectedCompanyId && (
                  <CheckIcon className="h-5 w-5 flex-shrink-0 text-accent" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer with count */}
      {!isLoading && filteredCompanies.length > 0 && (
        <div className="border-t border-line bg-ghost-bg p-2">
          <div className="text-center text-xs text-ink-muted">
            {filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'}
            {searchQuery && ' found'}
          </div>
        </div>
      )}
    </div>,
    document.body
  );

  if (!expanded) {
    return (
      <>
        <button
          ref={triggerRef}
          type="button"
          onClick={handleToggleOpen}
          aria-label="Select company"
          title={selectedCompany ? selectedCompany.name : 'Select company'}
          className={`flex w-full items-center justify-center rounded-full p-2.5 transition-colors hover:bg-canvas ${
            isOpen ? 'bg-nav-active text-accent' : 'text-ink'
          }`}
        >
          <BuildingOfficeIcon className="h-5 w-5" />
        </button>
        {dropdown}
      </>
    );
  }

  return (
    <div className="w-full">
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggleOpen}
        className="flex w-full items-center gap-2 rounded-full border border-line bg-surface px-3 py-2 text-left transition-colors hover:bg-ghost-bg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent"
      >
        <BuildingOfficeIcon className="h-5 w-5 flex-shrink-0 text-accent" />
        <span className="min-w-0 flex-1">
          {isLoading ? (
            <span className="block truncate text-sm text-ink-muted">Loading...</span>
          ) : selectedCompany ? (
            <span className="block truncate text-sm font-medium text-ink">{selectedCompany.name}</span>
          ) : (
            <span className="block truncate text-sm text-ink-muted">Select Company</span>
          )}
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 flex-shrink-0 text-ink-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {dropdown}
    </div>
  );
};

export default CompanySelector;
